import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts';
import { Box } from '../../Box/Box';
import { Flex } from '../../Flex/Flex';
import { Icon } from '../../Icon/Icon';
import { getAxisYDomain } from './getAxisYDomain';
import { SimpleTooltipProps } from './SimpleTooltip';

type GraphLineDatum = {
  date: number;
  [key: string]: number | string | undefined;
};

interface LineData {
  name: string;
  dataKey: string;
  stroke: string;
  strokeDasharray?: string;
  strokeWidth?: number;
  showDots?: boolean;
}

export type LineChartComponentProps = {
  data: GraphLineDatum[];
  lines: LineData[];
  xTickFormatter?: (tick: any) => string;
  height?: number | string;
  width?: number;
  componentTitle?: React.ReactNode;
  dataValueKeys?: string[];
  xScale?: 'time' | 'linear';
  yTickCount?: number;
  xTickCount?: number;
  yAxisWidth?: number;
  yDomain?: [number, number];
  CustomTooltip?: React.FC<SimpleTooltipProps>;
};

type GraphLineState = {
  data: GraphLineDatum[];
  left: number | string;
  right: number | string;
  refAreaLeft: string;
  refAreaRight: string;
  top: number | string;
  bottom: number | string;
};

export const GraphLine: React.FC<LineChartComponentProps> = ({
  data,
  lines,
  xTickFormatter,
  componentTitle,
  width,
  height = 400,
  dataValueKeys = ['value'],
  xScale = 'time',
  yTickCount,
  xTickCount,
  yAxisWidth = 45,
  yDomain,
  CustomTooltip,
}) => {
  const initialState: GraphLineState = {
    data: data,
    left: 'dataMin',
    right: 'dataMax',
    refAreaLeft: '',
    refAreaRight: '',
    top: yDomain ? yDomain[1] : 'dataMax+10',
    bottom: yDomain ? yDomain[0] : 'dataMin-1',
  };

  const [state, setState] = useState<GraphLineState>(initialState);

  const zoom = () => {
    let { refAreaLeft, refAreaRight } = state;

    if (refAreaLeft === refAreaRight || refAreaRight === '') {
      setState((prevState) => ({
        ...prevState,
        refAreaLeft: '',
        refAreaRight: '',
      }));
      return;
    }

    if (refAreaLeft > refAreaRight)
      [refAreaLeft, refAreaRight] = [refAreaRight, refAreaLeft];

    const [bottom, top] = getAxisYDomain(
      refAreaLeft,
      refAreaRight,
      dataValueKeys[0],
      1,
      state.data,
    );

    setState((prevState) => ({
      ...prevState,
      refAreaLeft: '',
      refAreaRight: '',
      left:
        xScale === 'time'
          ? new Date(refAreaLeft).getTime()
          : Number(refAreaLeft),
      right:
        xScale === 'time'
          ? new Date(refAreaRight).getTime()
          : Number(refAreaRight),
      bottom: bottom,
      top: top,
    }));
  };

  const zoomOut = () => {
    setState(initialState);
  };

  const getYDomain = (data: any[], dataKey: string) => {
    const values = data.map((item) => item[dataKey]);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.05;
    return [min - padding, max + padding];
  };

  useEffect(() => {
    if (state.data !== data) {
      if (yDomain) {
        setState({
          ...initialState,
          data,
          bottom: yDomain[0],
          top: yDomain[1],
        });
        return;
      }
      const [bottom, top] = getYDomain(data, dataValueKeys[0]);
      setState({ ...initialState, data, bottom, top });
    }
  }, [data, initialState, dataValueKeys, yDomain]);

  const { left, right, refAreaLeft, refAreaRight } = state;
  const hasZoom = left !== 'dataMin' || right !== 'dataMax';

  return (
    <Box className="highlight-bar-charts" userSelect="none" w="100%">
      <Flex justifyContent="space-between" mb={4}>
        <Box>{componentTitle || <Box />}</Box>
        <Icon
          icon="IcZoomOut"
          size="sm"
          onClick={zoomOut}
          wrapProps={{ opacity: hasZoom ? 1 : 0 }}
        />
      </Flex>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={state.data}
          height={typeof height === 'number' ? height : undefined}
          width={width}
          margin={{ left: 0 }}
          onMouseUp={zoom}
          onMouseDown={(e) =>
            setState((prevState) => ({
              ...prevState,
              refAreaLeft: e?.activeLabel || '',
            }))
          }
          onMouseMove={(e) =>
            state.refAreaLeft &&
            setState((prevState) => ({
              ...prevState,
              refAreaRight: e?.activeLabel || '',
            }))
          }
        >
          <CartesianGrid strokeDasharray="4 4" />
          <XAxis
            allowDataOverflow
            dataKey="date"
            domain={[left, right]}
            angle={-45}
            textAnchor="end"
            height={65}
            fontSize={10}
            tickMargin={10}
            tickFormatter={xTickFormatter}
            type="number"
            scale={xScale}
            tickCount={xTickCount}
          />
          <YAxis
            yAxisId="1"
            allowDataOverflow
            domain={[state.bottom, state.top]}
            fontSize={12}
            width={yAxisWidth}
            tick={{ fill: 'black' }}
            tickCount={yTickCount}
          />
          <Tooltip
            labelFormatter={xTickFormatter}
            content={
              CustomTooltip ? (
                <CustomTooltip labelFormatter={xTickFormatter} />
              ) : undefined
            }
          />
          {lines.map((line, index) => (
            <Line
              key={`${line.dataKey}-${index}`}
              yAxisId="1"
              type="linear"
              dataKey={line.dataKey}
              stroke={line.stroke}
              strokeWidth={line.strokeWidth ?? 2}
              strokeDasharray={line.strokeDasharray}
              dot={
                line.showDots === false
                  ? false
                  : (props) => (
                      <svg>
                        <circle
                          cx={props.cx}
                          cy={props.cy}
                          r={props.r * 1.5}
                          fill={props.stroke}
                        />
                      </svg>
                    )
              }
              animationDuration={300}
            />
          ))}
          {refAreaLeft && refAreaRight && (
            <ReferenceArea
              yAxisId="1"
              x1={refAreaLeft}
              x2={refAreaRight}
              strokeOpacity={0.3}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};
